(() => {
  const SELECTOR = '.rrr-desktop-header';

  class RRRDesktopHeader {
    constructor(root) {
      this.root = root;
      this.toggle = root.querySelector('[data-rrr-drawer-toggle]');
      this.closeButton = root.querySelector('[data-rrr-drawer-close]');
      this.drawer = root.querySelector('[data-rrr-drawer]');
      this.overlay = root.querySelector('[data-rrr-drawer-overlay]');
      this.tabs = [...root.querySelectorAll('[data-rrr-tab]')];
      this.audienceItems = [...root.querySelectorAll('[data-audience]')];
      this.activeAudience = 'men';
      this.lastFocused = null;
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundScroll = this.onScroll.bind(this);
      this.boundResize = this.onResize.bind(this);

      if (!this.toggle || !this.drawer || !this.overlay) return;

      this.toggle.addEventListener('click', () => this.toggleDrawer());
      this.closeButton?.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', () => this.close());

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setAudience(tab.dataset.rrrTab));
      });

      this.setAudience(this.activeAudience);
      window.addEventListener('scroll', this.boundScroll, { passive: true });
      window.addEventListener('resize', this.boundResize, { passive: true });
      this.onScroll();
    }

    isOpen() {
      return this.drawer.classList.contains('is-open');
    }

    toggleDrawer() {
      this.isOpen() ? this.close() : this.open();
    }

    open() {
      this.lastFocused = document.activeElement;
      this.overlay.hidden = false;

      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        this.overlay.classList.add('is-visible');
      });

      this.toggle.setAttribute('aria-expanded', 'true');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('rrr-desktop-drawer-open');
      document.addEventListener('keydown', this.boundKeydown);

      window.setTimeout(() => this.closeButton?.focus({ preventScroll: true }), 30);
    }

    close() {
      if (!this.isOpen()) return;

      this.drawer.classList.remove('is-open');
      this.overlay.classList.remove('is-visible');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('rrr-desktop-drawer-open');
      document.removeEventListener('keydown', this.boundKeydown);

      const duration = parseFloat(
        getComputedStyle(this.root).getPropertyValue('--rrr-animation-speed')
      ) || 260;

      window.setTimeout(() => {
        this.overlay.hidden = true;
      }, duration);

      this.lastFocused?.focus?.({ preventScroll: true });
    }

    setAudience(audience) {
      this.activeAudience = audience;

      this.tabs.forEach((tab) => {
        const active = tab.dataset.rrrTab === audience;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });

      this.audienceItems.forEach((item) => {
        const value = item.dataset.audience;
        item.hidden = !(value === audience || value === 'both');
      });
    }

    onScroll() {
      this.root.classList.toggle('is-scrolled', window.scrollY > 8);
    }

    onResize() {
      if (window.innerWidth < 990 && this.isOpen()) this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.isOpen()) return;

      const focusable = [
        ...this.drawer.querySelectorAll(
          'a[href], button:not([disabled]), summary, input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.rrrInitialized === 'true') return;
      root.dataset.rrrInitialized = 'true';
      new RRRDesktopHeader(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

/* Relocate existing Square Wishlist header control, retaining native app event handlers. */
(() => {
  const isWish = el => {
    if (!(el instanceof Element)) return false;
    const attributes = ['id', 'class', 'aria-label', 'title', 'href', 'data-testid', 'data-action'];
    return attributes.some(k => /wishlist|wish[-_ ]?list/i.test(el.getAttribute(k) || ''));
  };
  const init = (scope = document) => {
    scope.querySelectorAll('.rrr-desktop-header').forEach(header => {
      if (header.dataset.rrrWishMovedInit) return;
      header.dataset.rrrWishMovedInit = '1';
      const slot = header.querySelector('[data-rrr-wishlist-slot]');
      if (!slot) return;
      let finished = false, queued = false;
      const relocate = () => {
        queued = false;
        if (finished || innerWidth < 990) return;
        const headerRect = header.querySelector('.rrr-desktop-header__bar')?.getBoundingClientRect();
        if (!headerRect) return;
        for (const node of document.querySelectorAll(
          'a[href],button,[role="button"],[class*="wish" i],[id*="wish" i]'
        )) {
          if (slot.contains(node) || !isWish(node) ||
              node.closest('main,footer,.rrr-desktop-drawer,.rrr-mobile-header')) continue;
          const control = node.matches('a,button,[role="button"]')
            ? node : node.querySelector('a[href],button,[role="button"]') || node;
          const box = control.getBoundingClientRect();
          if (box.width < 8 || box.height < 8 ||
              box.top < headerRect.top - 65 || box.top > headerRect.bottom + 50) continue;
          observer.disconnect(); finished = true;
          slot.appendChild(control);
          control.classList.add('rrr-wishlist-app-icon');
          slot.classList.add('rrr-wishlist-slot--app-ready');
          break;
        }
      };
      const observer = new MutationObserver(() => {
        if (!finished && !queued) {
          queued = true;
          requestAnimationFrame(relocate);
        }
      });
      observer.observe(document.body, {childList: true, subtree: true});
      relocate();
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
